import { AlertTriangle } from 'lucide-react';
import type { InvestigationResponse, EntityRef, OpsBriefBlock } from '../../types/investigator';
import OpsBriefPanel from '../zoeAssistant/OpsBriefPanel';

/** Entity-type chips: categorical colour per kind (not status/provenance). */
const ENTITY_COLOURS: Record<string, string> = {
  order:    'bg-ring/20 text-ring border-ring/30',
  vehicle:  'bg-success/20 text-success border-success/30',
  driver:   'bg-brand/20 text-brand border-brand/30',
  location: 'bg-warning/20 text-warning border-warning/30',
};

function EntityChip({ entity }: { entity: EntityRef }) {
  const cls = ENTITY_COLOURS[entity.type] || 'bg-card text-foreground border-accent';
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-xs font-mono ${cls}`}>
      <span className="opacity-60">{entity.type}</span>
      <span>{entity.label || entity.id}</span>
    </span>
  );
}

interface AnswerPanelProps {
  data: InvestigationResponse;
}

export default function AnswerPanel({ data }: AnswerPanelProps) {
  // Use narrative from presentation layer when available; fall back to legacy fields.
  const narrative = data.presentation?.narrative;
  const opsBrief = data.presentation?.blocks?.find((b): b is OpsBriefBlock => b.type === 'ops_brief');

  return (
    <div className="space-y-4 h-full overflow-y-auto pr-1">
      {/* Headline / Summary */}
      <section>
        <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">Summary</h3>
        {narrative ? (
          <>
            <p className="text-foreground text-sm font-medium leading-relaxed mb-2">
              {narrative.answer?.trim() || narrative.headline}
            </p>
            {opsBrief ? <OpsBriefPanel block={opsBrief} /> : null}
          </>
        ) : (
          <>
            <p className="text-foreground text-sm leading-relaxed">{data.summary}</p>
            {data.detailed_explanation && (
              <p className="text-foreground text-sm leading-relaxed whitespace-pre-wrap mt-1">
                {data.detailed_explanation}
              </p>
            )}
          </>
        )}
      </section>

      {/* Entities */}
      {data.entities.length > 0 && (
        <section>
          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">Entities</h3>
          <div className="flex flex-wrap gap-2">
            {data.entities.map((e, i) => (
              <EntityChip key={`${e.type}-${e.id}-${i}`} entity={e} />
            ))}
          </div>
        </section>
      )}

      {/*
        Limitations — raw fallback only. When an ops_brief is present its own
        DATA QUALITY section already shows this same list, minus whichever
        lines the narrator classified as the subject's current state rather
        than a gap (those moved to OPERATIONAL STATUS instead — see
        `applyNoteClassification` in narrator.ts). Rendering the raw,
        unclassified list here too put the identical text under an amber
        "Data gaps" warning triangle a second time, undoing that
        classification for anyone using this panel specifically. Same
        precedence the Summary section above already gives ops_brief over
        the legacy fields.
      */}
      {!opsBrief && data.limitations.length > 0 && (
        <section>
          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">Data gaps</h3>
          <ul className="space-y-1">
            {data.limitations.map((l, i) => (
              <li key={i} className="flex items-start gap-2 text-xs text-warning">
                <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                {l}
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Metadata footer */}
      <div className="pt-2 border-t border-border text-xs text-muted-foreground flex flex-wrap gap-x-4 gap-y-1">
        <span>Intent: {data.intent}</span>
        <span>Evidence: {data.metadata.evidence_count}</span>
        <span>{data.metadata.execution_ms}ms</span>
        {data.metadata.narration_ms !== undefined && (
          <span>Narration: {data.metadata.narration_ms}ms</span>
        )}
      </div>
    </div>
  );
}
